import { Banknote, BanknoteX, FileX2, RefreshCcw, UserRoundMinus } from 'lucide-react';
import { Button } from '../../../../design-system/button/button';
import { Inline } from '../../../../design-system/inline/inline';
import { Stack } from '../../../../design-system/stack/stack';
import { cn } from '../../../../utils/cn';
import { exhaustiveCheck } from '../../../../utils/exhaustive-check';

export type MenuItemVariant =
  | 'MARK_PAID'
  | 'MARK_NOT_INVOICE'
  | 'EXCLUDE_SENDER'
  | 'MARK_NOT_PAID'
  | 'CHANGE_TRANSACTION_MATCH'
  | 'MATCH_TO_DIFFERENT_TRANSACTION'
  | 'I_HAVEN_T_PAID_THIS_INVOICE_YET'
  | 'DELETE_INVOICE';

type InvoiceTableRowOptionsMenuProps = {
  menuItems: MenuItemVariant[];
  onSelectMenuItem: (variant: MenuItemVariant) => void;
};

export const InvoiceTableRowOptionsMenu = ({ menuItems, onSelectMenuItem }: InvoiceTableRowOptionsMenuProps) => {
  return (
    <Stack space={3} align="stretch" className="bg-white px-4 py-3">
      {menuItems.map((item) => {
        return <OptionsMenuItem key={item} variant={item} onClick={() => onSelectMenuItem(item)} />;
      })}
    </Stack>
  );
};

type OptionsMenuItemProps = {
  variant: MenuItemVariant;
  onClick: () => void;
};

export const OptionsMenuItem = ({ variant, onClick }: OptionsMenuItemProps) => {
  const { icon: Icon, label } = getMenuItemContent(variant);

  return (
    <Button variant="ghost" size="none" className="w-full rounded-none" onClick={onClick}>
      <Inline space={3} className="w-full">
        {Icon && (
          <div className="h-4 w-4">
            <Icon strokeWidth={1} className="h-full w-full" />
          </div>
        )}

        <p
          className={cn('text-sm font-normal leading-6 tracking-tight text-black', {
            'text-seapoint-red': variant === 'DELETE_INVOICE',
          })}
        >
          {label}
        </p>
      </Inline>
    </Button>
  );
};

const getMenuItemContent = (variant: MenuItemVariant) => {
  switch (variant) {
    case 'MARK_PAID':
      return {
        icon: Banknote,
        label: 'Mark as paid',
      };
    case 'MARK_NOT_INVOICE':
      return {
        icon: FileX2,
        label: 'Mark as not invoice',
      };
    case 'EXCLUDE_SENDER':
      return {
        icon: UserRoundMinus,
        label: 'Exclude sender',
      };
    case 'MARK_NOT_PAID':
      return {
        icon: BanknoteX,
        label: 'Mark as not paid',
      };
    case 'CHANGE_TRANSACTION_MATCH':
      return {
        icon: RefreshCcw,
        label: 'Change transaction match',
      };
    case 'MATCH_TO_DIFFERENT_TRANSACTION':
      return {
        label: 'Match to different transaction',
      };
    case 'I_HAVEN_T_PAID_THIS_INVOICE_YET':
      return {
        label: "I haven't paid this invoice yet",
      };
    case 'DELETE_INVOICE':
      return {
        label: 'Delete invoice',
      };
    default:
      return exhaustiveCheck(variant);
  }
};
